/**
 * The ringing and live-call card for Quantis Voice, pinned above whatever
 * screen is open so a call can be answered or ended from anywhere in the app.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { PathIcon } from '../Icon';
import { useSharedPalette } from '../theme';
import { d_call, d_call_end, d_keyboard, d_mic, d_mic_off } from '../../shared/ui/iconPaths';
import { useQuantisVoice } from './VoiceContext';
import { DIAL_PAD, formatCallDuration, formatPhoneNumber } from './voiceFormat';

function useElapsed(since: number | null): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (since === null) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  return since === null ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}

function RoundButton({ label, icon, color, background, onPress, selected }: {
  label: string; icon: string; color: string; background: string; onPress: () => void; selected?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={selected === undefined ? undefined : { selected }}
      onPress={onPress}
      style={({ pressed }) => [styles.round, { backgroundColor: background, opacity: pressed ? 0.8 : 1 }]}
    >
      <PathIcon d={icon} size={22} color={color} />
    </Pressable>
  );
}

export function VoiceCallOverlay() {
  const voice = useQuantisVoice();
  const palette = useSharedPalette();
  const [keypad, setKeypad] = React.useState(false);
  const [tones, setTones] = React.useState('');
  const elapsed = useElapsed(voice?.call?.connectedAt ?? null);
  const callId = voice?.call?.id;
  React.useEffect(() => { setKeypad(false); setTones(''); }, [callId]);

  if (!voice || (!voice.incoming && !voice.call)) return null;

  const ringing = voice.incoming && !voice.call;
  const who = ringing ? voice.incoming! : voice.call!;
  const title = who.name || formatPhoneNumber(who.number);
  const line = ringing ? voice.incoming!.lineName : null;
  const subtitle = ringing
    ? [line ? `Call for ${line}` : 'Incoming call', who.name ? formatPhoneNumber(who.number) : null].filter(Boolean).join(' · ')
    : voice.call!.state === 'open'
      ? formatCallDuration(elapsed)
      : voice.call!.state === 'ringing' ? 'Ringing' : 'Calling';

  return (
    <View pointerEvents="box-none" style={styles.host}>
      <View
        accessibilityRole="alert"
        accessibilityLabel={`${ringing ? 'Incoming call from' : 'On a call with'} ${title}`}
        style={[styles.card, { backgroundColor: palette.raised, borderColor: palette.border }]}
      >
        <View style={styles.header}>
          <View style={styles.titles}>
            <Text numberOfLines={1} style={[styles.title, { color: palette.foreground }]}>{title}</Text>
            <Text numberOfLines={1} style={[styles.subtitle, { color: palette.muted }]}>{subtitle}</Text>
          </View>
          {ringing ? (
            <View style={styles.actions}>
              <RoundButton label="Decline" icon={d_call_end} color="#FFFFFF" background={palette.danger} onPress={voice.decline} />
              <RoundButton label="Answer" icon={d_call} color="#FFFFFF" background={palette.success} onPress={voice.accept} />
            </View>
          ) : (
            <View style={styles.actions}>
              <RoundButton
                label={voice.call!.muted ? 'Unmute' : 'Mute'}
                icon={voice.call!.muted ? d_mic_off : d_mic}
                selected={voice.call!.muted}
                color={voice.call!.muted ? palette.onBrand : palette.foreground}
                background={voice.call!.muted ? palette.brand : palette.secondary}
                onPress={voice.toggleMute}
              />
              <RoundButton
                label={keypad ? 'Hide keypad' : 'Keypad'}
                icon={d_keyboard}
                selected={keypad}
                color={keypad ? palette.onBrand : palette.foreground}
                background={keypad ? palette.brand : palette.secondary}
                onPress={() => setKeypad((v) => !v)}
              />
              <RoundButton label="Hang up" icon={d_call_end} color="#FFFFFF" background={palette.danger} onPress={voice.hangup} />
            </View>
          )}
        </View>
        {keypad && voice.call ? (
          <View style={styles.pad}>
            <Text style={[styles.tones, { color: palette.foreground }]} numberOfLines={1}>{tones || ' '}</Text>
            <View style={styles.padGrid}>
              {DIAL_PAD.map(({ key }) => (
                <Pressable
                  key={key}
                  accessibilityRole="button"
                  accessibilityLabel={`Send ${key}`}
                  onPress={() => { voice.sendDigits(key); setTones((t) => (t + key).slice(-24)); }}
                  style={({ pressed }) => [styles.padKey, { backgroundColor: pressed ? palette.track : palette.secondary }]}
                >
                  <Text style={[styles.padKeyText, { color: palette.foreground }]}>{key}</Text>
                </Pressable>
              ))}
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: { position: 'absolute', top: 12, left: 0, right: 0, alignItems: 'center', paddingHorizontal: 16, zIndex: 1000 },
  card: { width: '100%', maxWidth: 420, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, padding: 14, shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 18, shadowOffset: { width: 0, height: 6 }, elevation: 8 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  titles: { flex: 1, minWidth: 0 },
  title: { fontSize: 16, fontWeight: '600' },
  subtitle: { fontSize: 13, marginTop: 2, fontVariant: ['tabular-nums'] },
  actions: { flexDirection: 'row', gap: 10 },
  round: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  pad: { marginTop: 12, alignItems: 'center' },
  tones: { fontSize: 18, letterSpacing: 2, marginBottom: 8, fontVariant: ['tabular-nums'] },
  padGrid: { flexDirection: 'row', flexWrap: 'wrap', width: 228, gap: 12, justifyContent: 'center' },
  padKey: { width: 64, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  padKeyText: { fontSize: 20, fontWeight: '500' },
});
